/****************
 * WebSocket Hook for real-time stats and dashboard updates
 * Manages WebSocket connection lifecycle and direct cache writes
 ****************/

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getWebSocketUrl, DashboardSummaryResponse, TopTraderResponse, RecentTradeResponse, ActivePositionResponse } from '@/api/client';
import { withoutSimTraders } from '@/lib/simTraders';
import { useAuth } from '@/contexts/AuthContext';
import {
  transformSummaryToMetrics,
  transformTopTraders,
  transformRecentTrades,
  transformActivePositions,
  type TradeFeedPage,
} from '@/hooks/useDashboardData';
import type { LivePricesMap } from '@/hooks/useLivePrices';

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface WebSocketMessage {
  type: string;
  channel?: string;
  payload?: unknown;
  timestamp: number;
}

interface UseWebSocketOptions {
  enabled?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];

/****************
 * WHICH CACHED ENTRIES A BROADCAST IS ALLOWED TO OVERWRITE.
 *
 * These pushes are one payload computed once and sent to every connected
 * client, so they carry what the LEAST privileged viewer may see — binance
 * while MEXC is admin-only. The cache, meanwhile, holds one entry per venue.
 *
 * Writing with `exact: false` and nothing else wrote the binance payload over
 * every one of those entries, including an admin's `venue=all`. That is the
 * whole reason Guillaume could not see MEXC traders on the dashboard: the REST
 * fetch returned them correctly and the next socket tick, a second later,
 * replaced the list with the binance-only one. Nothing looked broken, because
 * nothing WAS broken twice in a row — it was correct, then overwritten.
 *
 * The payload now names its own venue and this matches it against the last
 * element of the query key, which is why every venue-scoped dashboard key ends
 * in its scope. A key that stops ending in its scope silently stops being
 * updated — it does not start being corrupted, which is the safer of the two
 * failures and the reason to match on position rather than search the key.
 *
 * A payload with no venue is treated as binance: that is a backend from before
 * this field existed, and binance is exactly what such a build broadcasts. It
 * happens for a few seconds during a deploy, and the cost is that an admin's
 * wider view waits for its next REST refetch instead of updating live.
 ****************/
function inVenue(queryKey: unknown[], venue?: string) {
  const carried = venue ?? 'binance';
  return {
    queryKey,
    exact: false,
    predicate: (query: { queryKey: readonly unknown[] }) =>
      query.queryKey[query.queryKey.length - 1] === carried,
  };
}

/****************
 * Main WebSocket hook for stats + dashboard updates
 ****************/

export function useStatsWebSocket(options: UseWebSocketOptions = {}) {
  const { enabled = true, onMessage, onConnect, onDisconnect, onError } = options;
  const queryClient = useQueryClient();
  const { user, getToken, isAdmin } = useAuth();

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const handleDashboardMessage = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      case 'dashboard_summary': {
        const summary = message.payload as DashboardSummaryResponse;
        if (!summary) break;

        // Set raw summary
        // Prefix match, not an exact key. The reading queries carry extra key
        // segments (limit, period, isAdmin), so an exact write lands on a key
        // nothing subscribes to and the socket update is silently discarded —
        // which is why these lists only ever refreshed on the 60s poll.
        queryClient.setQueriesData({ queryKey: ['dashboard', 'summary'], exact: false }, summary);
        // Set transformed metrics
        // Never onto a key narrowed by an asset type ('asset:<class>|<broker>'):
        // the broadcast summary is unfiltered, and writing it there would put
        // every type's figures under "Stocks/ETFs" until the next poll.
        // Only onto the key the broadcast IS: every venue, all time, no asset
        // filter (the server's BroadcastVenue). Written onto a Mexc or Binance
        // key, or a week's window, it put every venue's figures under the
        // admin's chosen one (2026-10-11).
        queryClient.setQueriesData({
          predicate: (q) => q.queryKey[0] === 'dashboard-metrics'
            && q.queryKey[1] === 'all'
            && (q.queryKey[2] === 'all' || q.queryKey[2] === 'default')
            && !q.queryKey.some((k) => typeof k === 'string' && k.startsWith('asset:'))
            // Nor onto a group's lens (?set=): the broadcast is every trader's.
            && !q.queryKey.includes('lens'),
        }, transformSummaryToMetrics(summary));
        break;
      }

      case 'positions_update': {
        const data = message.payload as { positions?: ActivePositionResponse[]; count?: number; total?: number; venue?: string; timestamp?: number };
        if (!data?.positions) break;

        // Write to every cached ['dashboard','active-positions',<limit>] key,
        // not one hardcoded limit — the REST hook's limit doesn't have to match
        // whatever number is hardcoded here, and a mismatch means the "live"
        // stream silently updates a cache entry no screen reads (2026-08-27,
        // mobile audit §16.1: the same trap already caught top_traders/positions
        // once with different limits on each side).
        const transformed = {
          positions: transformActivePositions(data.positions),
          total: data.total ?? data.positions.length,
          truncated: (data.total ?? data.positions.length) > data.positions.length,
        };
        queryClient.setQueriesData(inVenue(['dashboard', 'active-positions'], data.venue), transformed);
        break;
      }

      case 'recent_trades': {
        const data = message.payload as { trades?: RecentTradeResponse[]; count?: number; venue?: string; timestamp?: number };
        if (!data?.trades) break;

        const transformed = transformRecentTrades(data.trades);
        /*
         * First pages only, cut to their size, in the paged shape. The feed
         * pages since 2026-10-07 (['dashboard','recent-trades', limit, period,
         * offset, scope, ...]); the broadcast is the newest trades, so writing
         * it into page 7 would show page 1 there. The total stays what the
         * last read said until the next poll.
         */
        const carried = data.venue ?? 'binance';
        queryClient.setQueriesData<TradeFeedPage>({
          queryKey: ['dashboard', 'recent-trades'],
          exact: false,
          predicate: (q) => q.queryKey[q.queryKey.length - 1] === carried && q.queryKey[4] === 0,
        }, (prev) => {
          const pageSize = typeof prev === 'undefined' ? transformed.length : Math.max(prev.rows.length, 1);
          return { rows: transformed.slice(0, pageSize), total: prev?.total ?? transformed.length };
        });
        break;
      }

      case 'top_traders': {
        const data = message.payload as { traders?: TopTraderResponse[]; count?: number; venue?: string; timestamp?: number };
        if (!data?.traders) break;

        const transformed = transformTopTraders(data.traders);
        // Re-apply the SIM filter. Now that this write actually reaches the
        // cache, pushing the raw payload would put CopyFi's own test portfolios
        // back onto a non-admin's leaderboard on the next socket tick — undoing
        // the filter the query itself applies.
        queryClient.setQueriesData(
          inVenue(['dashboard', 'top-traders'], data.venue),
          withoutSimTraders(transformed, isAdmin, (t: any) => t.trader_id),
        );
        break;
      }

      case 'prices_update': {
        const prices = message.payload as LivePricesMap;
        if (!prices) break;

        queryClient.setQueryData<LivePricesMap>(['live-prices'], (old) => ({
          ...old,
          ...prices,
        }));
        break;
      }

      default:
        break;
    }
  }, [queryClient]);

  const connect = useCallback(async () => {
    if (!enabled) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    setConnectionState('connecting');
    const generation = generationRef.current;

    /*
     * The session token, not the user id (2026-10-21). This fetched the token
     * and then sent only user_id, which the server used to believe, so anyone
     * could connect as anyone. The server now takes the user from the verified
     * token and ignores user_id; a browser cannot set headers on a websocket
     * upgrade, so the token rides in the URL. It expires in about a minute.
     */
    let url = getWebSocketUrl();
    if (user?.id) {
      const token = await getToken();
      if (token) {
        url += `?token=${encodeURIComponent(token)}`;
      }
    }

    // disconnect() ran while the token was being fetched: the page is gone.
    if (generation !== generationRef.current) return;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnectionState('connected');
      reconnectAttemptRef.current = 0;
      onConnect?.();

      // Subscribe to user-specific updates if authenticated
      if (user?.id) {
        ws.send(JSON.stringify({
          type: 'subscribe',
          payload: { channel: 'user' },
        }));
      }
    };

    ws.onmessage = (event) => {
      try {
        // Handle potential batched messages (newline-separated)
        const messages = event.data.split('\n').filter(Boolean);

        for (const msgStr of messages) {
          const message: WebSocketMessage = JSON.parse(msgStr);
          setLastMessage(message);
          onMessage?.(message);

          // Handle dashboard + price push messages (direct cache write)
          handleDashboardMessage(message);

          // Handle legacy stats updates - invalidate relevant queries
          if (message.type === 'stats_update' && message.payload) {
            const payload = message.payload as { metric?: string; horizon?: string };
            if (payload.metric && payload.horizon) {
              queryClient.invalidateQueries({
                queryKey: ['stats', payload.metric, payload.horizon],
              });
              queryClient.invalidateQueries({
                queryKey: ['stats', 'bulk', payload.horizon],
              });
              queryClient.invalidateQueries({
                queryKey: ['stats', 'summary'],
              });
            }
          }
        }
      } catch (error) {
        console.error('[WebSocket] Failed to parse message:', error);
      }
    };

    ws.onerror = (event) => {
      setConnectionState('error');
      onError?.(event);
    };

    /****************
     * ONLY THE SOCKET THIS HOOK STILL OWNS MAY RECONNECT.
     *
     * Closing on unmount fires onclose too, and it used to schedule a
     * reconnect: every page visit left a socket nobody owned, reconnecting
     * forever and writing every push into the cache. The tab reached 4 GB
     * (Guillaume, 2026-10-09).
     ****************/
    ws.onclose = () => {
      if (wsRef.current !== ws) return;
      setConnectionState('disconnected');
      wsRef.current = null;
      onDisconnect?.();

      // Attempt reconnection with exponential backoff
      if (enabled) {
        const delay = RECONNECT_DELAYS[Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)];
        reconnectAttemptRef.current++;

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };
  }, [enabled, user?.id, getToken, queryClient, onConnect, onDisconnect, onError, onMessage, handleDashboardMessage]);

  const disconnect = useCallback(() => {
    generationRef.current++;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionState('disconnected');
  }, []);

  const sendMessage = useCallback((type: string, payload?: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Reconnect when user changes
  useEffect(() => {
    if (enabled && connectionState === 'connected') {
      disconnect();
      connect();
    }
  }, [user?.id]);

  return {
    connectionState,
    lastMessage,
    isConnected: connectionState === 'connected',
    connect,
    disconnect,
    sendMessage,
  };
}

/****************
 * Hook for subscribing to specific channels
 ****************/

export function useWebSocketChannel(
  channel: string,
  onMessage: (payload: unknown) => void
) {
  const { sendMessage, isConnected, lastMessage } = useStatsWebSocket();

  useEffect(() => {
    if (isConnected) {
      sendMessage('subscribe', { channel });
    }

    return () => {
      if (isConnected) {
        sendMessage('unsubscribe', { channel });
      }
    };
  }, [isConnected, channel, sendMessage]);

  useEffect(() => {
    if (lastMessage?.channel === channel && lastMessage.payload) {
      onMessage(lastMessage.payload);
    }
  }, [lastMessage, channel, onMessage]);
}

/****************
 * Simple hook for connection status display
 ****************/

export function useWebSocketStatus() {
  const { connectionState, isConnected } = useStatsWebSocket({ enabled: true });

  return {
    status: connectionState,
    isConnected,
    statusText: connectionState === 'connected' ? 'Live' :
                connectionState === 'connecting' ? 'Connecting...' :
                connectionState === 'error' ? 'Connection Error' :
                'Disconnected',
  };
}
